/**
 * MedOS design tokens.
 *
 * These are transcribed from `design/MedOS Design System.dc.html` and the five
 * screen modules, and they are the ONLY place a hex value belongs. A colour
 * written inline in a feature screen is a review rejection: the design system
 * has to be changeable in one edit, and dark mode (post-MVP, PRD 7.4) depends
 * on every surface reading from here.
 *
 * Naming follows PRD 7.2/7.3 so a token can be traced back to the spec table.
 */

export const color = {
  brand: {
    /** Primary action, selected navigation, logo mark. Reserved for those. */
    primary: '#0F766E',
    primaryHover: '#0B5D67',
    /** Sidebar, headings, logo wordmark. */
    secondary: '#123047',
    /** Selected rows and subtle highlights. */
    soft: '#DFF5F2',
    /** Dashed accents on incomplete onboarding steps. */
    softBorder: '#A7D5CE',
  },

  surface: {
    page: '#F8FAFC',
    card: '#FFFFFF',
    /** Table headers and inset panels. */
    subtle: '#FCFDFE',
    /** Search fields, chips, inactive tabs. */
    muted: '#F1F5F9',
    /**
     * Inset panel on the navy totals card - the "رصيد سابق مستحق" block on the
     * checkout screen (MedOSFinance.dc.html).
     */
    onDarkInset: 'rgba(255,255,255,.08)',
    /** Highlighted statement row carrying the closing balance. */
    highlight: '#FFFBEB',
  },

  text: {
    primary: '#0F172A',
    /** Field labels and secondary body copy. */
    secondary: '#334155',
    muted: '#64748B',
    /** Placeholders and disabled text. */
    faint: '#94A3B8',
    onDark: '#FFFFFF',
    /** Muted text on the navy sidebar. */
    onDarkMuted: '#8FAAC0',
    /** Idle navigation item on the navy sidebar. */
    onDarkItem: '#C8D8E4',
    /** Body copy on navy marketing panels. */
    onDarkBody: '#AFC4D2',
    /** Lowest-contrast text on the TV screen. */
    onDarkFaint: '#5E7D95',
    /**
     * The legal footnote at the bottom of the navy marketing panel on the login
     * screen ("MedOS is not a hospital information system").
     * `MedOSAuthPublic.dc.html` line 28.
     */
    onDarkFootnote: '#7593AC',
  },

  border: {
    default: '#D8E1E7',
    /** Dividers inside a card, lighter than the card's own border. */
    subtle: '#EEF3F6',
    /**
     * Between rows of a long data table - lighter still than `subtle`, so a
     * 25-row registry does not read as a grid. Used by the patient registry
     * (`MedOSOps.dc.html`, patients screen).
     */
    row: '#F4F7F9',
    /** Inputs and chips on a muted background. */
    muted: '#E2E8F0',
    strong: '#CBD5E1',
    onDark: 'rgba(255,255,255,.09)',
    /** Divider and outline-button border inside the navy totals card. */
    onDarkStrong: 'rgba(255,255,255,.25)',
    /** Rule between rows of the totals breakdown on the navy card. */
    onDarkSubtle: 'rgba(255,255,255,.15)',
  },

  /**
   * Semantic states. `fg` is text/icon, `bg` is the tinted surface, `border`
   * is the outline where the state needs one.
   *
   * Colour is never the only signal (PRD 7.3): every consumer of these also
   * renders text, and critical states add an icon.
   */
  status: {
    success: { fg: '#15803D', bg: '#DCFCE7', border: '#86EFAC' },
    info: { fg: '#2563EB', bg: '#DBEAFE', border: '#93C5FD' },
    infoStrong: '#1E40AF',
    /** Glyph on a blue tile - the image-document badge on the patient profile. */
    infoIcon: '#1D4ED8',
    warning: { fg: '#B45309', bg: '#FEF3C7', border: '#FCD34D' },
    warningStrong: '#92400E',
    /**
     * Darkest amber. Body copy inside a filled warning panel, where
     * `warningStrong` on `warning.bg` is short of 4.5:1. Used by the branch
     * deactivation notice in `design/MedOSAdmin.dc.html`.
     */
    warningDeep: '#78350F',
    danger: { fg: '#B91C1C', bg: '#FEF2F2', border: '#FCA5A5' },
    dangerChip: '#FEE2E2',
    /**
     * Heading of a panel whose whole subject is dangerous - the allergies and
     * conditions card on the patient profile. Darker than `danger.fg` so the
     * title still outranks the red rows beneath it.
     */
    dangerTitle: '#7F1D1D',
    /** Supporting detail inside a red row (reaction, last occurrence). */
    dangerBody: '#991B1B',
    /**
     * The filled red surface of a warning that cannot be dismissed: the
     * critical medication banner on the prescription screen, and the critical
     * lab-value banner. Deliberately a solid dark fill rather than a tinted
     * one - `design/MedOSClinical.dc.html` makes these the only inverted
     * surfaces in the clinical module, and that is the point (PRD 7.4: a
     * critical alert must be impossible to mistake for an informational one).
     */
    dangerSolid: '#7F1D1D',
    /** Body copy on `dangerSolid`. White is too harsh for a paragraph. */
    dangerSolidBody: '#FECACA',
    /** Lowest-contrast supporting line on `dangerSolid`: rule version, source. */
    dangerSolidMuted: '#FCA5A5',
    /** Body copy on an amber banner: the merge screen's match-evidence notice. */
    warningBanner: '#78350F',
    neutral: { fg: '#475569', bg: '#F1F5F9', border: '#E2E8F0' },
    /** Ready-for-checkout. Deliberately not teal: teal means "primary action". */
    checkout: { fg: '#6D28D9', bg: '#EDE9FE', border: '#C4B5FD' },
  },

  /** Non-semantic accents that appear in specific surfaces. */
  accent: {
    /** "Doctor on duty" / live indicator dot on the sidebar. */
    online: '#34D399',
    /** Platform-support impersonation banner (PRD 7.4: unmistakable). */
    impersonationBg: '#7C2D12',
    impersonationFg: '#FFEDD5',
    impersonationDot: '#FB923C',
    /**
     * Receivables-ageing bands (FIN-023). Four steps from healthy to overdue;
     * the middle step has no semantic-palette equivalent, which is why it lives
     * here rather than being approximated with `status.warning`.
     */
    ageing61to90: '#EA580C',
    /** Public waiting-room TV screen. */
    tvBg: '#0B2233',
    /**
     * The next-up cards on the TV screen. Lighter than `border.onDarkInset`
     * (.08) because the TV is read from across a room and the card has to read
     * as a surface rather than a hairline (design/MedOSAuthPublic.dc.html, `tv`).
     */
    tvCardBg: 'rgba(255,255,255,.07)',
    /** Outline of the "exit display mode" control on the TV screen. */
    tvControlBorder: 'rgba(255,255,255,.14)',
    /** Device frame on the public booking preview. */
    deviceBg: '#0F172A',
    deviceBezel: '#1E293B',
  },
} as const;

export const font = {
  /** Arabic-first. Tajawal covers Arabic and Latin at matching weights. */
  sans: `"Tajawal","Tajawal Fallback",system-ui,-apple-system,"Segoe UI",sans-serif`,
  /**
   * Everything that must stay Latin and aligned: MRNs, invoice numbers, phone
   * numbers, times, lab values, money. Always paired with `dir="ltr"` so a
   * clinical code does not reorder inside Arabic text.
   */
  mono: `"IBM Plex Mono","Courier New",monospace`,
} as const;

export const fontSize = {
  xxs: '10px',
  xs: '11px',
  sm: '12px',
  base: '13px',
  md: '14px',
  lg: '15px',
  xl: '16px',
  '2xl': '17px',
  '3xl': '19px',
  '4xl': '21px',
  '5xl': '26px',
  '6xl': '32px',
  display: '44px',
} as const;

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
  black: 800,
} as const;

export const radius = {
  xs: '5px',
  sm: '7px',
  md: '9px',
  lg: '11px',
  xl: '12px',
  '2xl': '14px',
  '3xl': '16px',
  pill: '999px',
} as const;

export const space = {
  0: '0',
  1: '4px',
  2: '6px',
  3: '9px',
  4: '12px',
  5: '14px',
  6: '16px',
  7: '18px',
  8: '20px',
  9: '22px',
  10: '26px',
  12: '32px',
  14: '40px',
  16: '48px',
} as const;

export const shadow = {
  card: '0 1px 2px rgba(15,23,42,.04)',
  dropdown: '0 12px 32px rgba(15,23,42,.12)',
  modal: '0 24px 70px rgba(15,23,42,.30)',
  drawer: '-12px 0 40px rgba(15,23,42,.16)',
  /** Mirrored for LTR, where the drawer opens from the right. */
  drawerLtr: '12px 0 40px rgba(15,23,42,.16)',
} as const;

export const layout = {
  sidebarWidth: '250px',
  topbarHeight: '60px',
  drawerWidth: '400px',
  contentMaxWidth: '1200px',
  /** WCAG 2.2 AA target size for interactive controls on touch surfaces. */
  minTouchTarget: '44px',
} as const;

export const zIndex = {
  stickyHeader: 15,
  topbar: 20,
  drawerScrim: 80,
  drawer: 81,
  commandPalette: 90,
  modal: 95,
  /** Dropdowns rendered to <body>, which must clear a modal they open from. */
  popover: 96,
  toast: 100,
} as const;

export const motion = {
  /** Kept short: these are operational screens used all day. */
  fast: '.12s',
  base: '.18s',
  easing: 'cubic-bezier(.4,0,.2,1)',
} as const;

/**
 * Every animated surface reads this. `prefers-reduced-motion` is a WCAG 2.2
 * requirement (PRD 21.6), not a nicety, and honouring it in one place means a
 * feature screen cannot forget.
 */
export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export type Direction = 'rtl' | 'ltr';

/**
 * Logical-direction helper.
 *
 * The screens are laid out in Arabic first, so "start" is the right edge under
 * RTL and the left edge under LTR. Using this instead of hard-coded left/right
 * is what keeps the LTR build from mirroring incorrectly (DS-003).
 */
export const dirValue = <T>(dir: Direction, rtl: T, ltr: T): T => (dir === 'rtl' ? rtl : ltr);
